import { hasClass } from "../dom";
import TreeLink from "./tree-link";
import { Coordinates, Dimensions, TreeDataNode, TreeOptions } from "./tree-models";
import TreeNode from "./tree-node";

export class TreeRenderer {
  protected treeNodeClass = TreeNode;
  protected treeLinkClass = TreeLink;

  // settings
  private debug: boolean;
  private debugNodeData: boolean;
  private enablePanningCursor: boolean;
  private nodeMargin: Coordinates; // px
  private linkStartOffset: Coordinates;
  private linkEndOffset: Coordinates;
  private rootPosition: Coordinates;
  private showRoot: boolean;
  private zoom: number; // multiplier
  private zoomMax: number;
  private zoomMin: number;

  // data
  private generationsDepth: number[];
  protected inputNodes: TreeDataNode;
  protected treeNodes: Map<string, TreeNode>;

  // DOM
  private drawingContainer: HTMLElement;
  private htmlClasses: { [key: string]: string };
  protected forbiddenMousePanTargets: string[];
  private lastMousePosition: Coordinates;
  private size: Dimensions; // px
  private renderTimes: {
    layout: number;
    links: number;
    nodes: number;
    transformations: number;
    total: number;
  };
  private isMouseDown: boolean;

  constructor(protected treeContainer: HTMLElement) {
    // settings; use "this.setOptions()" to configure
    this.debug = false;
    this.debugNodeData = false;
    this.nodeMargin = { x: 60, y: 5 };
    this.linkStartOffset = { x: 0, y: 20 };
    this.linkEndOffset = { x: 1, y: 20 };
    this.rootPosition = { x: 0, y: 0 };
    this.showRoot = true;
    this.zoom = 1;
    this.zoomMax = 4;
    this.zoomMin = 0.25;

    // data
    // "this.inputNodes" is set using "this.setNodeData()"
    // The rest are used internally. You are not supposed to touch them.
    this.generationsDepth = [0];
    this.inputNodes = { id: "ROOT", depth: 0 } as TreeDataNode;
    this.size = { height: 0, width: 0 };
    this.treeNodes = new Map<string, TreeNode>();

    // DOM/HTML stuff; used internally
    this.htmlClasses = {
      drawingContainer: "tree-renderer-drawing-container",
      panning: "tree-panning",
    };
    this.forbiddenMousePanTargets = [];

    // pan events
    this.lastMousePosition = { x: undefined, y: undefined };
    this.treeContainer.onmousedown = (e) => this.handleMousePanStart(e);
    // attaching to "window" allows dragging even outside of the browser window
    window.addEventListener("mousemove", this.handleMousePan);
    window.addEventListener("mouseup", this.handleMousePanEnd);

    // zoom events
    this.treeContainer.onwheel = (e) => this.handleMouseZoom(e);
  }

  destroy() {
    window.removeEventListener("mousemove", this.handleMousePan);
    window.removeEventListener("mouseup", this.handleMousePanEnd);
  }

  /**
   * process
   * A utility function that traverses the tree and runs a "processor" function on each node. Useful to avoid
   * writing the same recursive pattern over and over again.
   *
   * The "processor" would recieve the child and the parent nodes as arguments, whenever they are available.
   *
   * @param {TreeDataNode} thisNode
   * @param {CallableFunction} processor
   * @param {TreeDataNode} parentNode
   * @return {void}
   */
  protected process(thisNode: TreeDataNode, processor: CallableFunction, parentNode?: TreeDataNode): void {
    processor(thisNode, parentNode);
    thisNode.children?.forEach((childNode) => this.process(childNode, processor, thisNode));
  }

  /**
   * handleMousePanStart
   * Detects what element the user is trying to drag and initializes tree panning, if necessary.
   *
   * @param {MouseEvent} event
   * @return {void}
   */
  private handleMousePanStart = (event: MouseEvent): void => {
    if (event.target["tagName"] === "A" || event.target["tagName"] === "IMG" || hasClass(event.target as HTMLElement, this.forbiddenMousePanTargets)) {
      this.isMouseDown = false;
      return;
    }

    if (this.enablePanningCursor) {
      this.treeContainer.classList.add(this.htmlClasses.panning);
    }

    this.isMouseDown = true;
    this.lastMousePosition = {
      x: this.lastMousePosition.x === undefined ? event.screenX : this.lastMousePosition.x,
      y: this.lastMousePosition.y === undefined ? event.screenY : this.lastMousePosition.y,
    };
  };

  /**
   * handleMousePan
   * Handles "dragging" using the mouse to pan the tree.
   *
   * Note that "onMouseDown" + "onMouseMove" combination is used, instead of "onDrag",
   * because "onDrag" does not work on transparent elements or containers.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  private handleMousePan = (event: MouseEvent): void => {
    const isLeftMouseButton = event.buttons === 1;
    if (!isLeftMouseButton || !this.isMouseDown) {
      return;
    }

    event.preventDefault();

    const movementX = event.screenX - this.lastMousePosition.x;
    const movementY = event.screenY - this.lastMousePosition.y;
    this.rootPosition = {
      x: this.rootPosition.x + movementX / this.zoom,
      y: this.rootPosition.y + movementY / this.zoom,
    };

    this.lastMousePosition = { x: event.screenX, y: event.screenY };

    this.applyTransformations();
  };

  /**
   * handleMousePanEnd
   * Ends panning with the mouse.
   *
   * @param {void}
   * @return {void}
   */
  private handleMousePanEnd = () => {
    this.isMouseDown = false;
    if (this.enablePanningCursor) {
      this.treeContainer.classList.remove(this.htmlClasses.panning);
    }

    this.lastMousePosition = { x: undefined, y: undefined };
  };

  /**
   * handleMouseZoom
   * Handles scroll events on this.treeContainer and translates them into zoom in/out.
   *
   * @param {WheelEvent} event
   * @return {void}
   */
  private handleMouseZoom(event: WheelEvent): void {
    event.preventDefault();

    // Calculate the mouse position relative to "this.treeContainer". The mouse position is the transform origin,
    // because we want the tree to stay "centered" where the mouse is pointing (google maps/OSM/others zoom feel).
    const treeContainerRect = this.treeContainer.getBoundingClientRect();
    const mousePosition = {
      x: event.clientX - treeContainerRect.left + this.treeContainer.scrollLeft,
      y: event.clientY - treeContainerRect.top + this.treeContainer.scrollTop,
    };

    // origin before applying zoom
    const oldOrigin = {
      x: mousePosition.x / this.zoom,
      y: mousePosition.y / this.zoom,
    };

    // Touchpads tend to send low deltaY values at high rate, while mice send high deltaY at low rate.
    // This piece of code normalizes the calculated zoomRatio for similar feel on all devices.
    // Using a constant ratio is not an option, because it eliminates the "acceleration" effect.
    const maxZoomRatio = 0.1;
    const eventZoomRatio = 0.0025 * event.deltaY;
    const normalizedZoomRatio = Math.abs(eventZoomRatio) > maxZoomRatio ? maxZoomRatio * Math.sign(eventZoomRatio) : eventZoomRatio;

    this.setZoom(this.zoom - normalizedZoomRatio * this.zoom);

    // origin after validating and applying zoom
    const newOrigin = {
      x: mousePosition.x / this.zoom,
      y: mousePosition.y / this.zoom,
    };

    // Use the difference between the new origin and the old origin, where the mouse is, to
    // move the entire tree. This way it looks like the origin under the mouse has not moved
    // when we applied scaling.
    this.rootPosition = {
      x: this.rootPosition.x + newOrigin.x - oldOrigin.x,
      y: this.rootPosition.y + newOrigin.y - oldOrigin.y,
    };

    this.applyTransformations();
  }

  /**
   * initDrawingContainer
   * Initilizes a new empty this.drawingContainer as a new <div> element, to be used as a drawing canvas.
   * If the container has already been initialized, it only clears the current container.
   *
   * @param {void}
   * @return {void}
   */
  private initDrawingContainer(): void {
    this.drawingContainer = this.drawingContainer || document.querySelector(`.${this.htmlClasses.drawingContainer}`);

    if (!this.drawingContainer) {
      this.drawingContainer = document.createElement("div");
      this.drawingContainer.setAttribute("data-test-id", "whole-tree-selector");
      this.drawingContainer.className = this.htmlClasses.drawingContainer;
      this.treeContainer.append(this.drawingContainer);
    } else {
      this.drawingContainer.textContent = "";
    }
  }

  /**
   * resetDrawingContainerSize
   * Readjusts the width and height of this.drawingContainer to fit all rendered nodes.
   *
   * @param {void}
   * @return {void}
   */
  private resetDrawingContainerSize(): void {
    this.size.height = this.size.height || this.treeContainer.clientHeight;
    this.size.width = this.size.width || this.treeContainer.clientWidth;

    this.treeNodes.forEach((n) => {
      if (n.isHidden) {
        return;
      }
      this.size.height = Math.max(this.size.height, n.position.y + n.branchWidth);
      this.size.width = Math.max(this.size.width, n.position.x + this.generationsDepth[this.generationsDepth.length - 1]);
    });

    this.drawingContainer.style.height = `${this.size.height}px`;
    this.drawingContainer.style.width = `${this.size.width}px`;
  }

  /**
   * renderChildrenLinks
   * Renders a link from one node to another.
   * This function requires the layout to be calculated first.
   *
   * @param {TreeDataNode} child
   * @param {TreeDataNode} parent
   * @return {void}
   */
  private renderLink(child: TreeDataNode, parent: TreeDataNode): void {
    if (!child || !parent || this.treeNodes.get(child.id).isHidden === true) {
      return;
    }

    if (this.showRoot || parent.id !== this.getRootId()) {
      const link = new this.treeLinkClass(this.treeNodes.get(parent.id), this.treeNodes.get(child.id), this.linkStartOffset, this.linkEndOffset);
      link.renderIn(this.drawingContainer);
    }
  }

  /**
   * hideChildrenNodes
   * Sets the "hidden" property of a TreeNode and all its children, grandchildren and so on,
   * based on the given data hierarchy.
   *
   * @param {TreeDataNode} node
   * @return {void}
   */
  private hideChildrenNodes(node: TreeDataNode): void {
    node.children?.forEach((c) => {
      this.hideChildrenNodes(c);
      this.treeNodes.get(c.id).isHidden = true;
    });
  }

  /**
   * createNode
   * Takes an input TreeDataNode, converts it to the internal TreeNode and saves it in the internal node map.
   *
   * @param {node} TreeDataNode
   * @return {void}
   */
  protected createNode(node: TreeDataNode): void {
    if (!this.treeNodes.has(node.id)) {
      this.treeNodes.set(node.id, new this.treeNodeClass(node));
    }
  }

  /**
   * renderNode
   * Renders a TreeNode in this.drawingContainer. Note that layout is not applied here.
   *
   * @see this.render(), for an example how to position the nodes properly.
   * @param {nodeId} string
   * @return {void}
   */
  private renderNode(nodeId: string): void {
    if (this.showRoot || nodeId !== this.getRootId()) {
      this.treeNodes.get(nodeId).renderIn(this.drawingContainer);
    }
  }

  /**
   * calculateNodeSize
   * Takes the TreeNode dimensions computed by the browser when adding each node to the DOM, then determines the width of all
   * tree branches and the depth of each node generation, so that we can compute the tree layout and avoid overlapping nodes.
   *
   * Note that you only need to call this function once with the root node. All children will be processed recursively.
   *
   * @param {TreeDataNode} rootNode
   * @return {void}
   */
  private calculateNodeSize(rootNode: TreeDataNode): void {
    const thisNode = this.treeNodes.get(rootNode.id);
    if (!thisNode) {
      throw new Error(`TreeNode: "${rootNode.id}" is not rendered yet. Cannot calculate layout size. Call this.renderNodes() first.`);
    }

    if (thisNode.isHidden) {
      thisNode.branchWidth = 0;
      return;
    }

    // "depth" is the size of the largest node in this generation (across all parents)
    const thisNodeDepth = thisNode.clientSize.width > 0 ? 2 * this.nodeMargin.x + thisNode.clientSize.width : 0;
    const thisGenerationDepth = this.generationsDepth[rootNode.depth] || 0;
    this.generationsDepth[rootNode.depth] = Math.max(thisGenerationDepth, thisNodeDepth);

    // "width" when there are no children is the size of the node itself
    const thisNodeWidth = thisNode.isHidden ? 0 : thisNode.clientSize.height + 2 * this.nodeMargin.y;

    // "width" when there are children is the sum of the sizes of all children nodes
    let allChildrenWidth = 0;
    rootNode.children?.forEach((c) => {
      this.calculateNodeSize(c);
      const child = this.treeNodes.get(c.id);
      allChildrenWidth += child.branchWidth;
    });

    thisNode.branchWidth = Math.max(thisNodeWidth, allChildrenWidth);
  }

  /**
   * calculateLayout
   * Uses the tree parameters calculated in this.calculateNodeSize() to calculate the position of every node.
   * Note: you only need to call this once with the root node and no other parameters. The function will process
   * all children recursively.
   *
   * @param {TreeDataNode} rootNode
   * @param {string} parentId
   * @param {string} previousSiblingId
   * @return {void}
   */
  private calculateLayout(rootNode: TreeDataNode, parentId?: string, previousSiblingId?: string): void {
    const thisNode = this.treeNodes.get(rootNode.id);
    if (thisNode.isHidden) {
      return;
    }

    const parentNode = this.treeNodes.get(parentId);
    const previousSibling = this.treeNodes.get(previousSiblingId);

    thisNode.position = {
      // X = parent X shifted by the size (the depth) of the parent
      // The root has no parent, so we assume its X = 0. Also there is no "-1 generation", before it, so we
      // consider the shift to be 0.
      x: (parentNode?.position.x || 0) + (this.generationsDepth[rootNode.depth - 1] || 0),
      // 1. If there is a child before me, then: Y = previousSibling.Y + previousSibling.branchWidth
      //    Explanation:
      //    Go to the sibling above me, then skip down through all its children (branchWidth). Right below them is my Y.
      //    If there my top sibling has no children, then its "branchWidth" is 0.
      // 2a. If there is no "previousSibling", then Y = parent.Y.
      //    Explanation:
      //    I am the first child in the array, this is why there is no "previous". In this case, I must align to my
      //    parent
      // 2b. If there is no "previousSibling" and no parent, I am the root, so my Y is 0.
      y: (previousSibling?.position.y || parentNode?.position.y || 0) + (previousSibling?.branchWidth || 0),
    };

    if (rootNode.children) {
      for (let i = 0; i < rootNode.children.length; i++) {
        this.calculateLayout(rootNode.children[i], rootNode.id, rootNode.children[i - 1]?.id);
      }
    }
  }

  /**
   * applyLayout
   * Moves the nodes to their respective coordinates, after they have been calculated in this.calculateLayout().
   *
   * @param {void}
   * @return {void}
   */
  private applyLayout(): void {
    this.treeNodes.forEach((n) => {
      if (!n.isHidden) n.translateToPosition();
    });
  }

  /**
   * resetLayout
   * Deletes all previously calculated node positions and dimensions.
   *
   * @param {void}
   * @return {void}
   */
  private resetLayout(): void {
    this.treeNodes.clear();
    this.generationsDepth = [0];
  }

  /**
   * applyTransformations
   * Applies the current zoom and pan values.
   *
   * @param {void}
   * @return {void}
   */
  private applyTransformations(): void {
    const scale = `scale3d(${this.zoom}, ${this.zoom}, 1)`;
    const translate = `translate3d(${Math.round(this.rootPosition.x)}px, ${Math.round(this.rootPosition.y)}px, 0)`;

    this.drawingContainer.style.transform = `${scale} ${translate}`;
    this.drawingContainer.style.transformOrigin = "0 0";
  }

  /**
   * findInputNode
   * Searches the input nodes hieararchy for a node with a given ID and returns it. If there is no match,
   * it returns null.
   *
   * @param {string} nodeId
   * @param {TreeDataNode} rootNode
   * @returns {TreeDataNode | null}
   */
  protected findInputNode(nodeId: string, rootNode: TreeDataNode): TreeDataNode | null {
    if (rootNode.id === nodeId) {
      return rootNode;
    }

    return rootNode.children?.map((c) => this.findInputNode(nodeId, c)).filter((n) => n)[0] || null;
  }

  /**
   * getRootId
   * Self-explanatory
   *
   * @param {void}
   * @return {string}
   */
  private getRootId(): string {
    return this.inputNodes.id;
  }

  /**
   * setZoom
   *
   * @param {number} zoom
   * @return {this}
   */
  private setZoom(zoom: number): this {
    this.zoom = zoom !== undefined ? zoom : this.zoom;
    this.zoom = this.zoom > this.zoomMax ? this.zoomMax : this.zoom;
    this.zoom = this.zoom < this.zoomMin ? this.zoomMin : this.zoom;

    return this;
  }

  /**
   * setZoomConstraints
   *
   * @param {number} zoomMax
   * @param {number} zoomMin
   * @return {this}
   */
  private setZoomConstraints(zoomMax: number, zoomMin: number): this {
    const max = zoomMax !== undefined ? zoomMax : this.zoomMax;
    const min = zoomMin !== undefined ? zoomMin : this.zoomMin;

    if (max < min) {
      throw new Error("The maximum Tree zoom must be greater than the minimum zoom.");
    }

    if (min < 0) {
      throw new Error("Tree zoom can not be negative.");
    }

    this.zoomMax = max;
    this.zoomMin = min;

    return this;
  }

  /**
   * foldAllNodes
   * Folds all nodes, but the root (or the first generation of children, in case the root is hidden). We skip them,
   * otherwise the tree would become completely invisible.
   *
   * Use this.render() to actually apply the operation.
   *
   * @param {void}
   * @return {this}
   */
  public foldAllNodes(): this {
    this.process(this.inputNodes, (n) => {
      if (this.showRoot || n.id !== this.getRootId()) {
        n.folded = true;
      }
    });

    return this;
  }

  /**
   * unfoldAllNodes
   * Use this.render() to actually show them.
   *
   * @param {void}
   * @return {this}
   */
  public unfoldAllNodes(): this {
    this.process(this.inputNodes, (n) => (n.folded = false));

    return this;
  }

  /**
   * toggleFoldNode
   * Toggles the fold state of an input node.
   *
   * @param {string} nodeId
   * @return {this}
   */
  public toggleFoldNode(nodeId: string): this {
    const node = this.findInputNode(nodeId, this.inputNodes);
    if (!node) {
      throw new Error(`Cannot fold node with ID: "${nodeId}". No such node.`);
    }

    node.folded = !node.folded;

    return this;
  }

  /**
   * setOptions
   * Configures the tree before rendering. Options can be changed multiple times, you just need to call "this.render()" to apply them.
   *
   * @param {TreeOptions} options
   * @return {this}
   */
  public setOptions(options: TreeOptions): this {
    this.resetLayout();

    this.debug = options.debug !== undefined ? options.debug : this.debug;
    this.debugNodeData = options.debugNodeData !== undefined ? options.debugNodeData : this.debugNodeData;
    this.enablePanningCursor = options.enablePanningCursor !== undefined ? options.enablePanningCursor : this.enablePanningCursor;
    this.nodeMargin = options.nodeMargin !== undefined ? options.nodeMargin : this.nodeMargin;
    this.linkEndOffset = options.linkEndOffset !== undefined ? options.linkEndOffset : this.linkEndOffset;
    this.linkStartOffset = options.linkStartOffset !== undefined ? options.linkStartOffset : this.linkStartOffset;
    this.rootPosition = options.rootPosition !== undefined ? options.rootPosition : this.rootPosition;
    this.showRoot = options.showRoot !== undefined ? options.showRoot : this.showRoot;

    this.setZoomConstraints(options.zoomMax, options.zoomMin).setZoom(options.zoom);

    return this;
  }

  public getOptions(): TreeOptions {
    return {
      enablePanningCursor: this.enablePanningCursor,
      nodeMargin: this.nodeMargin,
      linkEndOffset: this.linkEndOffset,
      linkStartOffset: this.linkStartOffset,
      rootPosition: this.rootPosition,
      showRoot: this.showRoot,
      zoom: this.zoom,
      zoomMax: this.zoomMax,
      zoomMin: this.zoomMin,
    };
  }

  /**
   * setNodeData
   * Sets the input nodes. The data structure is compatible with the D3 format, except that "depth" property is required on all nodes.
   * It can be used multiple times to display different trees. You only need to call "this.render()" again.
   *
   * @param {TreeNodeData} nodeData
   * @return {this}
   */
  public setNodeData(nodeData: TreeDataNode): this {
    this.resetLayout();
    this.inputNodes = nodeData;

    return this;
  }

  /**
   * render
   * Does all the necessary data processing and calculations to transform the input TreeDataNodes and display them in "this.drawingContainer",
   * taking into account the configuration set using "this.setOptions()".
   *
   * @param {void}
   * @return {void}
   */
  public render(): void {
    const renderStartTime = Date.now();
    this.renderTimes = {
      nodes: 0,
      layout: 0,
      links: 0,
      transformations: 0,
      total: 0,
    };

    try {
      // reset everything to the initial state
      this.initDrawingContainer();
      this.treeNodes.forEach((n) => (n.isHidden = false));

      // generate node HTML
      let phaseStartTime = Date.now();
      this.process(this.inputNodes, (n) => this.createNode(n));
      this.process(this.inputNodes, (n) => n.folded === true && this.hideChildrenNodes(n));
      this.process(this.inputNodes, (n) => this.renderNode(n.id));
      this.renderTimes.nodes = Date.now() - phaseStartTime;

      // position properly all nodes in a tree-shaped layout
      phaseStartTime = Date.now();
      this.calculateNodeSize(this.inputNodes);
      this.calculateLayout(this.inputNodes);
      this.applyLayout();
      this.renderTimes.layout = Date.now() - phaseStartTime;

      // link the nodes
      phaseStartTime = Date.now();
      this.process(this.inputNodes, (child, parent) => this.renderLink(child, parent));
      this.renderTimes.links = Date.now() - phaseStartTime;

      // apply zoom, pan and dimensions to the entire tree
      phaseStartTime = Date.now();
      this.applyTransformations();
      this.resetDrawingContainerSize();
      this.renderTimes.transformations = Date.now() - phaseStartTime;
    } catch (e) {
      const err = new Error(`Failed rendering a tree. ${e.message}`);
      err.stack = e.stack;
      throw err;
    }

    this.renderTimes.total = Date.now() - renderStartTime;
    if (this.debug) {
      this.renderDebugText();
    }
  }

  /**
   * renderDebugText
   * Don't use it in production.
   */
  private renderDebugText(): void {
    // eslint-disable-next-line no-console
    console.log(
      `* Tree Debug Info *
      Render Finished Timestamp: ${Date.now()}
      Render Execution Times (ms):
        * nodes: ${this.renderTimes.nodes} ms
        * links: ${this.renderTimes.links} ms
        * layout: ${this.renderTimes.layout} ms
        * transformations: ${this.renderTimes.transformations} ms
        * other: ${this.renderTimes.total - this.renderTimes.nodes - this.renderTimes.links - this.renderTimes.layout - this.renderTimes.transformations} ms
        * total: ${this.renderTimes.total} ms
      Tree Dimensions: ${JSON.stringify(this.size)}
      Node Margins: ${JSON.stringify(this.nodeMargin)}
      Show Root: ${this.showRoot ? "yes" : "no"}
      Position: [${this.rootPosition.x}, ${this.rootPosition.y}]
      Zoom: ${this.zoomMin} <= ${this.zoom} <= ${this.zoomMax}
      Node Count: ${this.treeNodes.size}`
    );

    if (this.debugNodeData) {
      // eslint-disable-next-line no-console
      console.log("\nTreeDataNodes:", this.inputNodes, "\nTreeNodes:", this.treeNodes);
    }
  }
}
